import strings from 'strings'

const STATUS_THRESHOLDS = [
  { min: 7, label: 'HEALTHY' },
  { min: 4, label: 'WATCH' },
  { min: 0, label: 'AT RISK' }
]

export const STATUS_STYLES = {
  'AT RISK': { accent: 'var(--status-pending)', chipBg: 'rgba(232,115,74,0.16)', chipText: 'var(--status-pending)' },
  WATCH: { accent: '#9b8afb', chipBg: 'rgba(155,138,251,0.18)', chipText: '#9b8afb' },
  HEALTHY: { accent: '#4caf82', chipBg: 'rgba(76,175,130,0.16)', chipText: '#4caf82' }
}

export const BANNER_BG = '#152420'
export const ACCENT_PURPLE = '#9b8afb'
export const ACCENT_ORANGE = 'var(--status-pending)'
export const GRAY_BAR = '#c9ced1'

export const THRESHOLDS = {
  CURRENT_RATIO_COMFORT: 1.5,
  CURRENT_RATIO_FLOOR: 1,
  DEBT_TO_EQUITY: 2,
  AR_CONCENTRATION: 60,
  AR_IMPAIRMENT: 0.2
}

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)

export const getHealthStatusLabel = (healthScore) => {
  const score = num(healthScore)
  return (STATUS_THRESHOLDS.find((tier) => score >= tier.min) || STATUS_THRESHOLDS[STATUS_THRESHOLDS.length - 1]).label
}

export const getStatusStyle = (statusLabel) => STATUS_STYLES[statusLabel] || STATUS_STYLES.HEALTHY

export const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(num(amount))

export const formatSignedCurrency = (amount) => `${num(amount) >= 0 ? '+' : ''}${formatCurrency(amount)}`

export const formatMultiple = (value) => `${num(value).toFixed(2)}x`

export const formatPercent = (value, digits = 1) => `${num(value).toFixed(digits)}%`

export const formatSignedPercent = (value, digits = 1) => `${num(value) >= 0 ? '+' : ''}${num(value).toFixed(digits)}%`

export const formatPeriodLabel = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export const formatPeriodLabelLong = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export const readMetrics = (doc) => {
  const insights = doc?.insights || {}
  const leverage = insights.leverageSolvency || {}
  const liquidity = insights.liquidityPosition || {}
  const receivables = insights.receivablesConcentration || {}

  const totalAssets = num(leverage.totalAssets)
  const totalLiabilities = num(leverage.totalLiabilities)
  const totalDebt = num(leverage.totalDebt)
  const totalEquity = num(leverage.totalEquity)
  const totalAR = num(receivables.totalAR)

  const arPct = receivables.ARpercentageOverTotalAssets !== undefined
    ? num(receivables.ARpercentageOverTotalAssets)
    : (totalAssets ? (totalAR / totalAssets) * 100 : 0)

  const claimsTotal = totalLiabilities + totalEquity

  return {
    date: doc?.date || '',
    error: doc?.error || null,
    validBalance: doc?.validBalance !== false,
    healthScore: num(insights.health),
    healthVerdictText: insights.healthVeredict || '',
    takeaways: insights.takeaways || [],
    totalAssets,
    totalLiabilities,
    totalDebt,
    totalEquity,
    totalAR,
    arPct,
    otherAssets: Math.max(0, totalAssets - totalAR),
    debtToEquityRatio: num(leverage.debtToEquityRatio),
    currentRatio: num(liquidity.currentRatio),
    workingCapital: num(liquidity.workingCapital),
    liabilitiesPctOfAssets: totalAssets ? (totalLiabilities / totalAssets) * 100 : 0,
    equityPctOfAssets: totalAssets ? (totalEquity / totalAssets) * 100 : 0,
    claimsTotal,
    balanceGap: totalAssets - claimsTotal,
    debtLiabilitySpread: totalDebt - totalLiabilities
  }
}

const pickHeadline = (metrics) => {
  if (metrics.totalEquity <= 0) return strings.BALANCE_SHEET_HEADLINE_INSOLVENT
  if (metrics.workingCapital < 0) return strings.BALANCE_SHEET_HEADLINE_ILLIQUID
  if (metrics.debtToEquityRatio > THRESHOLDS.DEBT_TO_EQUITY || metrics.currentRatio < THRESHOLDS.CURRENT_RATIO_COMFORT) {
    return strings.BALANCE_SHEET_HEADLINE_THIN
  }
  return strings.BALANCE_SHEET_HEADLINE_SOLID
}

export const deriveBalanceSheetVerdict = (doc, otherDoc) => {
  const metrics = readMetrics(doc)
  const statusLabel = getHealthStatusLabel(metrics.healthScore)
  const otherMetrics = otherDoc ? readMetrics(otherDoc) : null

  const assetsNote = metrics.totalAR
    ? `${strings.BALANCE_SHEET_AR_SHORT} ${formatCurrency(metrics.totalAR)} · ${formatPercent(metrics.arPct)} ${strings.BALANCE_SHEET_OF_ASSETS}`
    : strings.BALANCE_SHEET_NO_AR_REPORTED

  const currentRatioNote = metrics.currentRatio
    ? `${strings.BALANCE_SHEET_CURRENT_RATIO} ${formatMultiple(metrics.currentRatio)} · ${metrics.currentRatio < THRESHOLDS.CURRENT_RATIO_FLOOR
      ? `${strings.BALANCE_SHEET_UNDER} ${THRESHOLDS.CURRENT_RATIO_FLOOR.toFixed(2)}`
      : metrics.currentRatio < THRESHOLDS.CURRENT_RATIO_COMFORT
        ? `${strings.BALANCE_SHEET_UNDER} ${THRESHOLDS.CURRENT_RATIO_COMFORT.toFixed(2)}`
        : `${strings.BALANCE_SHEET_ABOVE} ${THRESHOLDS.CURRENT_RATIO_COMFORT.toFixed(2)}`}`
    : strings.BALANCE_SHEET_NO_CURRENT_RATIO

  const leverageNote = metrics.debtToEquityRatio > THRESHOLDS.DEBT_TO_EQUITY
    ? `${strings.BALANCE_SHEET_ABOVE_THE} ${THRESHOLDS.DEBT_TO_EQUITY.toFixed(2)} ${strings.BALANCE_SHEET_THRESHOLD}`
    : `${strings.BALANCE_SHEET_WITHIN_THE} ${THRESHOLDS.DEBT_TO_EQUITY.toFixed(2)} ${strings.BALANCE_SHEET_THRESHOLD}`

  let equityNote = `${formatPercent(metrics.equityPctOfAssets)} ${strings.BALANCE_SHEET_OF_TOTAL_ASSETS}`
  if (otherMetrics && otherMetrics.totalEquity) {
    const change = ((metrics.totalEquity - otherMetrics.totalEquity) / Math.abs(otherMetrics.totalEquity)) * 100
    if (Math.abs(change) >= 0.05) {
      equityNote = `${formatSignedPercent(change)} ${strings.BALANCE_SHEET_VS} ${formatPeriodLabel(otherMetrics.date)}`
    }
  }

  return {
    metrics,
    statusLabel,
    eyebrow: `${formatPeriodLabelLong(metrics.date).toUpperCase()} · ${strings.BALANCE_SHEET_VERDICT_LABEL}`,
    headline: pickHeadline(metrics),
    description: metrics.healthVerdictText || metrics.takeaways[0] || '',
    tileNotes: {
      assets: assetsNote,
      liabilities: `${formatPercent(metrics.liabilitiesPctOfAssets)} ${strings.BALANCE_SHEET_OF_TOTAL_ASSETS}`,
      workingCapital: currentRatioNote,
      leverage: leverageNote,
      equity: equityNote
    }
  }
}

export const deriveComposition = (metrics) => {
  const buildColumn = (segments) => {
    const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0)
    return {
      total,
      segments: segments.map((segment) => ({
        ...segment,
        pct: total > 0 ? (Math.max(0, segment.value) / total) * 100 : 0,
        sharePct: total > 0 ? (segment.value / total) * 100 : 0
      }))
    }
  }

  return {
    assets: buildColumn([
      { key: 'ar', label: strings.BALANCE_SHEET_ACCOUNTS_RECEIVABLE, value: metrics.totalAR, color: BANNER_BG },
      { key: 'other', label: strings.BALANCE_SHEET_OTHER_ASSETS, value: metrics.otherAssets, color: GRAY_BAR }
    ]),
    claims: buildColumn([
      { key: 'liabilities', label: strings.BALANCE_SHEET_TOTAL_LIABILITIES_LABEL, value: metrics.totalLiabilities, color: ACCENT_ORANGE },
      { key: 'equity', label: strings.BALANCE_SHEET_TOTAL_EQUITY_LABEL, value: metrics.totalEquity, color: ACCENT_PURPLE }
    ])
  }
}

export const deriveHealthIndicators = (metrics) => {
  const indicators = []

  if (metrics.currentRatio) {
    const belowFloor = metrics.currentRatio < THRESHOLDS.CURRENT_RATIO_FLOOR
    const belowComfort = metrics.currentRatio < THRESHOLDS.CURRENT_RATIO_COMFORT
    indicators.push({
      title: `${strings.BALANCE_SHEET_CURRENT_RATIO} ${formatMultiple(metrics.currentRatio)}`,
      subtext: belowFloor
        ? strings.BALANCE_SHEET_INDICATOR_RATIO_BELOW_FLOOR
        : belowComfort
          ? strings.BALANCE_SHEET_INDICATOR_RATIO_BELOW_COMFORT
          : strings.BALANCE_SHEET_INDICATOR_RATIO_HEALTHY,
      warning: belowComfort
    })
  }

  indicators.push({
    title: metrics.workingCapital >= 0
      ? strings.BALANCE_SHEET_INDICATOR_WC_POSITIVE
      : strings.BALANCE_SHEET_INDICATOR_WC_NEGATIVE,
    subtext: formatSignedCurrency(metrics.workingCapital),
    warning: metrics.workingCapital < 0
  })

  if (metrics.debtToEquityRatio) {
    const levered = metrics.debtToEquityRatio > THRESHOLDS.DEBT_TO_EQUITY
    indicators.push({
      title: `${strings.BALANCE_SHEET_DEBT_TO_EQUITY_LABEL} ${formatMultiple(metrics.debtToEquityRatio)}`,
      subtext: levered
        ? `${strings.BALANCE_SHEET_INDICATOR_LEVERAGE_ABOVE} ${formatCurrency(metrics.totalDebt)} ${strings.BALANCE_SHEET_INDICATOR_AGAINST_EQUITY} ${formatCurrency(metrics.totalEquity)}.`
        : strings.BALANCE_SHEET_INDICATOR_LEVERAGE_WITHIN,
      warning: levered
    })
  }

  if (metrics.totalAR) {
    const concentrated = metrics.arPct > THRESHOLDS.AR_CONCENTRATION
    indicators.push({
      title: `${strings.BALANCE_SHEET_AR_IS} ${formatPercent(metrics.arPct)} ${strings.BALANCE_SHEET_OF_TOTAL_ASSETS}`,
      subtext: concentrated
        ? `${strings.BALANCE_SHEET_INDICATOR_AR_ABOVE} ${THRESHOLDS.AR_CONCENTRATION}% ${strings.BALANCE_SHEET_INDICATOR_CONCENTRATION_THRESHOLD}`
        : `${strings.BALANCE_SHEET_INDICATOR_AR_BELOW} ${THRESHOLDS.AR_CONCENTRATION}% ${strings.BALANCE_SHEET_INDICATOR_CONCENTRATION_THRESHOLD}`,
      warning: concentrated
    })
  }

  indicators.push({
    title: metrics.totalEquity > 0
      ? strings.BALANCE_SHEET_INDICATOR_EQUITY_POSITIVE
      : strings.BALANCE_SHEET_INDICATOR_EQUITY_NEGATIVE,
    subtext: `${formatCurrency(metrics.totalEquity)}, ${formatPercent(metrics.equityPctOfAssets)} ${strings.BALANCE_SHEET_OF_TOTAL_ASSETS}.`,
    warning: metrics.totalEquity <= 0
  })

  if (Math.abs(metrics.debtLiabilitySpread) > 1) {
    indicators.push({
      title: strings.BALANCE_SHEET_INDICATOR_DEBT_SPREAD,
      subtext: `${strings.BALANCE_SHEET_INDICATOR_DEBT_ON_FILE} ${formatCurrency(metrics.totalDebt)} ${strings.BALANCE_SHEET_VS} ${formatCurrency(metrics.totalLiabilities)} ${strings.BALANCE_SHEET_INDICATOR_IN_LIABILITIES}`,
      warning: true
    })
  }

  return indicators
}

export const deriveBalanceCheck = (metrics) => {
  const totalsSum = Math.abs(metrics.balanceGap) < 1
  const spread = `${formatCurrency(metrics.totalAssets)} ${strings.BALANCE_SHEET_IN_ASSETS_AGAINST} ${formatCurrency(metrics.claimsTotal)} ${strings.BALANCE_SHEET_IN_CLAIMS} (${formatSignedCurrency(metrics.balanceGap)}).`

  if (metrics.validBalance && totalsSum) {
    return {
      reconciles: true,
      text: `${strings.BALANCE_SHEET_BOTH_COLUMNS_TOTAL} ${formatCurrency(metrics.totalAssets)} — ${strings.BALANCE_SHEET_CHECK_HOLDS}`
    }
  }
  if (metrics.validBalance) {
    return { reconciles: false, text: `${strings.BALANCE_SHEET_CHECK_PASSED_TOTALS_DIFFER} ${spread}` }
  }
  return { reconciles: false, text: `${strings.BALANCE_SHEET_CHECK_FAILED} ${spread}` }
}

export const deriveTrajectory = (metrics) => {
  if (!(metrics.totalAR > 0 && metrics.totalEquity > 0)) return null
  const impairment = metrics.totalAR * THRESHOLDS.AR_IMPAIRMENT
  const shareOfEquity = (impairment / metrics.totalEquity) * 100
  const consequence = shareOfEquity >= 100
    ? strings.BALANCE_SHEET_TRAJECTORY_WIPES_EQUITY
    : `${strings.BALANCE_SHEET_TRAJECTORY_CONSUMES} ${formatPercent(shareOfEquity)} ${strings.BALANCE_SHEET_TRAJECTORY_OF_EQUITY}`
  return `${formatPercent(metrics.arPct)} ${strings.BALANCE_SHEET_TRAJECTORY_PREFIX} ${Math.round(THRESHOLDS.AR_IMPAIRMENT * 100)}% ${strings.BALANCE_SHEET_TRAJECTORY_IMPAIRMENT} (${formatCurrency(impairment)}) ${consequence}`
}

export const deriveComparisonVerdict = (report, fromMetrics, toMetrics) => {
  const fromScore = fromMetrics.healthScore
  const toScore = toMetrics.healthScore
  const headline = toScore > fromScore
    ? strings.BALANCE_SHEET_TREND_IMPROVING
    : toScore < fromScore
      ? strings.BALANCE_SHEET_TREND_DETERIORATING
      : strings.BALANCE_SHEET_TREND_STABLE
  const description = (report.takeawayAnalysis && report.takeawayAnalysis[0]) || ''
  return { headline, description, fromScore, toScore, improved: toScore >= fromScore }
}

export const splitFlag = (text) => {
  const separator = text.indexOf(':')
  if (separator === -1) return { title: text, body: '' }
  return { title: text.slice(0, separator).trim(), body: text.slice(separator + 1).trim() }
}
